import React, { ComponentProps, ReactNode } from 'react';

import { Alert, Box, Stack, Typography } from '@mui/material';

import GridObjectFieldTemplate from './GridObjectFieldTemplate';
import UserDetailsFormSection from './UserDetailsFormSection';

type FormSection = {
  id: string;
  title: string;
  description?: ReactNode;
  fields: Array<string>;
};

const SECTIONS: Array<FormSection> = [
  {
    id: 'contact',
    title: 'Personal & contact details',
    fields: [
      'customer_support_code',
      'title',
      'givenName',
      'surname',
      'dob',
      'email',
      'email_verified',
      'phone_prefix',
      'phone',
      'linked_personal_account',
    ],
  },
  {
    id: 'preferences',
    title: 'Location & preferences',
    fields: ['country_code', 'postcode', 'language', 'recently_used_airport_code'],
  },
  {
    id: 'partnerships',
    title: 'Frequent flyer accounts',
    fields: ['partnerships', 'frequent_flyer_numbers'],
  },
  {
    id: 'travel',
    title: 'Travel preferences',
    description: (
      <Alert severity="warning">
        Do not record health, medical, dietary, accessibility or other sensitive personal information here.
      </Alert>
    ),
    fields: ['travel_preferences'],
  },
  {
    id: 'access',
    title: 'Account access & roles',
    description: (
      <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 2, py: 1.5 }}>
        <Typography variant="body2" fontWeight={600}>
          Reset password has moved
        </Typography>
        <Typography variant="body2" color="text.primary">
          Use the Reset Password action in the Danger Zone bar at the top of this page.
        </Typography>
      </Box>
    ),
    fields: [
      'account_enabled',
      'flights_enabled',
      'referral_program_enabled',
      'gdpr',
      'shadow_ban_user',
      'roles',
      'vendors',
      'banned_led_user',
    ],
  },
  {
    id: 'business',
    title: 'Business details',
    fields: ['business_details'],
  },
];

const FULL_WIDTH_FIELDS = new Set([
  'roles',
  'vendors',
  'business_details',
  'travel_preferences',
  'partnerships',
  'linked_personal_account',
]);
const FIELD_COLUMNS: Record<string, number> = {
  dob: 12,
  email: 8,
  email_verified: 4,
  phone_prefix: 4,
  phone: 8,
};
const DESKTOP_FIELD_COLUMNS: Record<string, number> = {
  account_enabled: 8,
  flights_enabled: 8,
  referral_program_enabled: 8,
  gdpr: 8,
  shadow_ban_user: 8,
  customer_support_code: 5,
  title: 3,
  givenName: 8,
  surname: 8,
  email: 12,
  phone: 12,
  email_verified: 6,
  dob: 5,
  phone_prefix: 5,
};
const KNOWN_FIELDS = new Set(SECTIONS.flatMap((section) => section.fields));

export default function UserDetailsObjectFieldTemplate(props: ComponentProps<typeof GridObjectFieldTemplate>) {
  const { fieldPathId, properties } = props;
  // Only group the root. Nested objects retain their schema-driven fields and layouts.
  if (fieldPathId.path.length > 0) {
    return <GridObjectFieldTemplate {...props} />;
  }

  const additionalSection: FormSection = {
    id: 'additional',
    title: 'Additional details',
    fields: properties.filter((field) => !KNOWN_FIELDS.has(field.name)).map((field) => field.name),
  };

  const sections = [...SECTIONS, additionalSection]
    .map((section) => ({
      ...section,
      properties: section.fields.flatMap((name) => properties.filter((field) => field.name === name && !field.hidden)),
    }))
    .filter((section) => section.properties.length > 0);

  return (
    <Stack spacing={3} useFlexGap>
      {sections.map((section) => (
        <UserDetailsFormSection
          key={section.id}
          id={section.id}
          title={section.title}
          description={section.description}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(12, minmax(0, 1fr))',
                md: 'repeat(24, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            {section.properties.map((field) => (
              <Box
                key={field.name}
                sx={{
                  minWidth: 0,
                  ...(field.name === 'frequent_flyer_numbers' && { mt: -2 }),
                  gridColumn: FULL_WIDTH_FIELDS.has(field.name)
                    ? '1 / -1'
                    : {
                        sm: `span ${FIELD_COLUMNS[field.name] ?? 6}`,
                        md: `span ${DESKTOP_FIELD_COLUMNS[field.name] ?? (FIELD_COLUMNS[field.name] ?? 6) * 2}`,
                      },
                }}
              >
                {field.content}
              </Box>
            ))}
          </Box>
        </UserDetailsFormSection>
      ))}
      {properties
        .filter((field) => field.hidden)
        .map((field) => (
          <Box key={field.name} sx={{ display: 'none' }}>
            {field.content}
          </Box>
        ))}
    </Stack>
  );
}
